import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_STATS = { "HP": { current: 20, max: 20 } };

export default function WorldBuilder() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [world, setWorld] = useState({
    title: "",
    genre: "",
    tagline: "",
    lore: "",
    starting_prompt: "",
    mechanics_style: "freeform",
    custom_rules: "",
    color_theme: "crimson_gold",
    death_enabled: true,
    initial_stats: { ...DEFAULT_STATS },
    initial_inventory: [],
    is_anonymous: false,
  });
  const [statInput, setStatInput] = useState({ name: "", value: "" });
  const [invInput, setInvInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [isPublished, setIsPublished] = useState(false);

  useEffect(() => {
    (async () => {
      const m = await api.get("/meta/world-builder");
      setMeta(m.data);
      if (editing) {
        try {
          const { data } = await api.get(`/custom-worlds/${id}`);
          setWorld({
            title: data.title || "",
            genre: data.genre || "",
            tagline: data.tagline || "",
            lore: data.lore || "",
            starting_prompt: data.starting_prompt || "",
            mechanics_style: data.mechanics_style || "freeform",
            custom_rules: data.custom_rules || "",
            color_theme: data.color_theme || "crimson_gold",
            death_enabled: data.death_enabled !== false,
            initial_stats: data.initial_stats || {},
            initial_inventory: data.initial_inventory || [],
            is_anonymous: data.is_anonymous || false,
          });
          setIsPublished(data.is_published);
        } catch (e) {
          toast.error("Could not load world");
        }
      }
    })();
  }, [id, editing]);

  const set = (k, v) => setWorld((w) => ({ ...w, [k]: v }));

  const addStat = () => {
    if (!statInput.name.trim()) return;
    const num = Number(statInput.value);
    set("initial_stats", { ...world.initial_stats, [statInput.name.trim()]: isNaN(num) ? statInput.value : num });
    setStatInput({ name: "", value: "" });
  };

  const removeStat = (k) => {
    const next = { ...world.initial_stats };
    delete next[k];
    set("initial_stats", next);
  };

  const addInv = () => {
    if (!invInput.trim()) return;
    set("initial_inventory", [...world.initial_inventory, invInput.trim()]);
    setInvInput("");
  };

  const removeInv = (i) => {
    set("initial_inventory", world.initial_inventory.filter((_, idx) => idx !== i));
  };

  const save = async () => {
    if (world.title.length < 2 || world.lore.length < 20 || world.starting_prompt.length < 10) {
      toast.error("Title, lore (20+ chars), and starting prompt (10+ chars) are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...world, custom_rules: world.custom_rules?.trim() || null };
      if (editing) {
        await api.put(`/custom-worlds/${id}`, payload);
        toast.success("World saved");
      } else {
        const { data } = await api.post("/custom-worlds", payload);
        toast.success("World created");
        navigate(`/worlds/${data.world_id}`, { replace: true });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!editing) {
      toast.error("Save your world first");
      return;
    }
    if (!user.premium) {
      toast.error("Only Sovereigns may publish worlds");
      return;
    }
    setPublishing(true);
    try {
      if (isPublished) {
        await api.post(`/custom-worlds/${id}/unpublish`);
        setIsPublished(false);
        toast.success("Unpublished");
      } else {
        await api.post(`/custom-worlds/${id}/publish`);
        setIsPublished(true);
        toast.success("Published to the community");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  if (!meta) return null;

  return (
    <div className="min-h-screen relative z-10" data-testid="world-builder">
      <header className="border-b border-amber-500/20 backdrop-blur-md bg-stone-950/60 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <button data-testid="builder-back" onClick={() => navigate("/app")} className="btn-ghost text-[0.65rem]">← Vault</button>
          <div className="font-serif-display gold text-sm tracking-widest truncate">{editing ? "Edit World" : "Forge a World"}</div>
          <div className="flex items-center gap-2">
            {editing && (
              <button data-testid="publish-btn" onClick={publish} disabled={publishing || !user.premium}
                className={`btn-ghost text-[0.65rem] ${isPublished ? "border-emerald-500/50 text-emerald-300" : ""}`}
                title={user.premium ? "" : "Sovereigns only"}>
                {publishing ? "..." : isPublished ? "◆ Published" : "Publish"}
              </button>
            )}
            <button data-testid="save-btn" onClick={save} disabled={saving} className="btn-gold">
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        <Section title="Identity">
          <Field label="Title"><Input value={world.title} onChange={(v) => set("title", v)} testid="field-title" placeholder="Salt & Verdigris" maxLength={80} /></Field>
          <Field label="Genre"><Input value={world.genre} onChange={(v) => set("genre", v)} testid="field-genre" placeholder="Bronze-age piracy" maxLength={60} /></Field>
          <Field label="Tagline"><Input value={world.tagline} onChange={(v) => set("tagline", v)} testid="field-tagline" placeholder="One-line hook" maxLength={180} /></Field>
        </Section>

        <Section title="Lore & Opening">
          <Field label="Lore (world background)"><Textarea rows={8} value={world.lore} onChange={(v) => set("lore", v)} testid="field-lore" placeholder="A dying empire of tin-mines and cursed tides..." maxLength={4000} /></Field>
          <Field label="Starting Prompt (opening scene setup for the GM)"><Textarea rows={4} value={world.starting_prompt} onChange={(v) => set("starting_prompt", v)} testid="field-starting" placeholder="The player wakes bound in the hold of a ship..." maxLength={1000} /></Field>
        </Section>

        <Section title="Mechanics">
          <Field label="Preset Style">
            <select
              data-testid="field-mechanics"
              value={world.mechanics_style}
              onChange={(e) => set("mechanics_style", e.target.value)}
              className="w-full bg-stone-900/80 border border-amber-500/25 focus:border-amber-500 outline-none px-3 py-2 rounded-sm font-prose"
            >
              {meta.preset_styles.map((s) => (
                <option key={s.key} value={s.key}>{s.key.replace(/_/g, " ")}</option>
              ))}
              <option value="custom">Custom (write your own)</option>
            </select>
            <div className="mt-2 font-mono text-[0.65rem] tracking-wide text-stone-500 leading-relaxed">
              {meta.preset_styles.find((s) => s.key === world.mechanics_style)?.description || "Fully custom rules — write them below."}
            </div>
          </Field>
          <Field label="Optional GM Rules Override (leave blank to use the preset above)">
            <Textarea rows={5} value={world.custom_rules} onChange={(v) => set("custom_rules", v)} testid="field-custom-rules" placeholder="e.g. Track FATE points. On a roll of doubles, invoke a Muse..." maxLength={2000} />
          </Field>
          <div className="flex items-center gap-3">
            <input
              id="death"
              data-testid="field-death"
              type="checkbox"
              checked={world.death_enabled}
              onChange={(e) => set("death_enabled", e.target.checked)}
              className="w-5 h-5 accent-red-600"
            />
            <label htmlFor="death" className="font-serif-body py-2">
              Death enabled <span className="font-mono text-[0.65rem] text-stone-500 ml-2">(required for Hard Mode permadeath)</span>
            </label>
          </div>
        </Section>

        <Section title="Starting Character">
          <Field label="Stats">
            <div className="space-y-2">
              {Object.entries(world.initial_stats).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2">
                  <div className="flex-1 font-mono text-sm">{k}</div>
                  <div className="font-mono text-sm text-stone-400">{typeof v === "object" ? `${v.current}/${v.max}` : String(v)}</div>
                  <button onClick={() => removeStat(k)} className="text-stone-600 hover:text-red-400 text-sm w-10 h-10 flex items-center justify-center shrink-0" aria-label="Remove">✕</button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <input data-testid="stat-name" value={statInput.name} onChange={(e) => setStatInput({ ...statInput, name: e.target.value })} placeholder="Stat name (e.g. Sanity)" className="flex-1 bg-stone-900/80 border border-amber-500/25 px-2 py-2.5 sm:py-1.5 rounded-sm text-base sm:text-sm min-w-0" />
              <input data-testid="stat-value" value={statInput.value} onChange={(e) => setStatInput({ ...statInput, value: e.target.value })} placeholder="Value" className="w-20 sm:w-32 bg-stone-900/80 border border-amber-500/25 px-2 py-2.5 sm:py-1.5 rounded-sm text-base sm:text-sm min-w-0" />
              <button data-testid="add-stat" onClick={addStat} className="btn-ghost text-[0.65rem]">Add</button>
            </div>
            <div className="font-mono text-[0.6rem] text-stone-500 mt-2">Tip: for depleting resources like HP or Mana, add stat name (e.g. HP) and value "20" — the GM will treat it as a bar.</div>
          </Field>

          <Field label="Starting Inventory">
            <div className="space-y-1.5">
              {world.initial_inventory.map((item, i) => (
                <div key={`${item}-${i}`} className="flex items-center justify-between border-l border-amber-500/30 pl-2">
                  <div className="font-serif-body text-sm">{item}</div>
                  <button onClick={() => removeInv(i)} className="text-stone-600 hover:text-red-400 text-sm w-10 h-10 flex items-center justify-center shrink-0" aria-label="Remove">✕</button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <input data-testid="inv-input" value={invInput} onChange={(e) => setInvInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addInv()} placeholder="Item name" className="flex-1 bg-stone-900/80 border border-amber-500/25 px-2 py-2.5 sm:py-1.5 rounded-sm text-base sm:text-sm min-w-0" />
              <button data-testid="add-inv" onClick={addInv} className="btn-ghost text-[0.65rem]">Add</button>
            </div>
          </Field>
        </Section>

        <Section title="Aesthetic & Publishing">
          <Field label="Color Theme">
            <div className="flex flex-wrap gap-2">
              {meta.color_themes.map((c) => (
                <button
                  key={c}
                  data-testid={`theme-${c}`}
                  onClick={() => set("color_theme", c)}
                  className={`px-3 py-2 min-h-[44px] border rounded-sm font-mono text-[0.65rem] uppercase tracking-widest transition-colors ${
                    world.color_theme === c ? "border-amber-500 gold" : "border-stone-700 text-stone-400 hover:border-amber-500/50"
                  }`}
                >
                  {c.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          </Field>
          <div className="flex items-center gap-3">
            <input
              id="anon"
              data-testid="field-anonymous"
              type="checkbox"
              checked={world.is_anonymous}
              onChange={(e) => set("is_anonymous", e.target.checked)}
              className="w-5 h-5 accent-amber-500"
            />
            <label htmlFor="anon" className="font-serif-body py-2">
              Publish anonymously <span className="font-mono text-[0.65rem] text-stone-500 ml-2">(hide your name from other players)</span>
            </label>
          </div>
          {!user?.premium && (
            <div className="border border-amber-500/30 rounded-sm p-3 font-mono text-[0.7rem] text-amber-200/80">
              You can save as many drafts as you like. Publishing to the community requires Sovereign.
            </div>
          )}
        </Section>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="tome-card rounded-sm p-5 sm:p-6 space-y-5">
      <div className="font-mono text-[0.6rem] uppercase tracking-[0.4em] gold">{title}</div>
      {children}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="font-mono text-[0.65rem] uppercase tracking-widest text-stone-400 mb-2">{label}</div>
      {children}
    </div>
  );
}

function Input({ value, onChange, testid, placeholder, maxLength }) {
  return (
    <input
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      className="w-full bg-stone-900/80 border border-amber-500/25 focus:border-amber-500 outline-none px-3 py-2 rounded-sm font-prose text-stone-200 placeholder:text-stone-600"
    />
  );
}

function Textarea({ value, onChange, testid, placeholder, rows, maxLength }) {
  return (
    <textarea
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      maxLength={maxLength}
      className="w-full bg-stone-900/80 border border-amber-500/25 focus:border-amber-500 outline-none px-3 py-2 rounded-sm font-prose text-stone-200 placeholder:text-stone-600 leading-relaxed resize-y"
    />
  );
}
